import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core'
import { ChevronDown } from 'react-feather'
import { TFunction, useTranslation } from 'react-i18next'
import { EnergyCostsTable } from './EnergyCostsTable'
import { CostType } from '../../../generated/graphql'
import { CostRowDataForPrefill } from './cost-table-util'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { FixedCostsTable } from './FixedCostsTable'
import { CostRow, GroupedCost, isFixedCostRow } from './cost-table-types'
import { useCostTables } from './use-cost-tables'

type Props = {
  updateCost: (costId: string, versionId: string) => void
  createNewCost: (
    costRowData: CostRowDataForPrefill,
    versionId: string,
    groupedCostName: string,
  ) => void
  selectedVersions: SelectedVersion[]
}

export function sortGroupedCosts<T extends GroupedCost>(
  costs: T[],
  t: TFunction<string>,
): T[] {
  const getCostRowSortingValue = (row: CostRow): string => {
    if (isFixedCostRow(row)) {
      return row.providerName || row.description
    }
    return t(row.translationKey)
  }
  return costs
    .map((groupedCost) => ({
      ...groupedCost,
      costRows: groupedCost.costRows.sort((a, b) =>
        getCostRowSortingValue(a).localeCompare(getCostRowSortingValue(b)),
      ),
    }))
    .sort((a, b) =>
      (a.name || t('costs.table.noProvider')).localeCompare(
        b.name || t('costs.table.noProvider'),
      ),
    )
}

export function CostsTables(props: Props): React.ReactElement {
  const { updateCost, createNewCost, selectedVersions } = props
  const { t } = useTranslation()
  const { groupProjectCostsByCostType } = useCostTables()

  const groupedCostsByCostType = groupProjectCostsByCostType()

  return (
    <>
      <Accordion key="accordion-energy">
        <AccordionSummary expandIcon={<ChevronDown />}>
          <Typography variant="h6">
            {t(`costs.energyAccordionTitle`)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EnergyCostsTable versions={selectedVersions} />
        </AccordionDetails>
      </Accordion>
      {Object.keys(CostType).map((type) => (
        <Accordion key={`accordion-${type}`} data-cy={`accordion-${type}`}>
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Typography variant="h6">{t(`costType.${type}`)}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FixedCostsTable
              versions={selectedVersions}
              groupedCosts={sortGroupedCosts(
                groupedCostsByCostType[type as CostType],
                t,
              )}
              type={type as CostType}
              updateCost={updateCost}
              createNewCost={createNewCost}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}
